import { Button, Grid, InputAdornment, MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TextField, Checkbox, Select, DialCodes } from 'web-components-ts';
import { COLOR_PRIMARY } from '../../../attrs/colors';
import { LANGUAGES } from '../../../attrs/languages';
import { isError } from '../../../helpers/utils';
import { IUser } from '../../../types/interfaces/iUser';
import RouteLeavingCheck from '../../elements/RouteLeavingCheck';
import FormAlert from '../FormAlert';
import { ContainerForm } from './elements';

interface AccountFormFieldsProps {
  user: IUser;
  values: IUser;
  dirty: boolean;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | SelectChangeEvent<unknown>
  ) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
  errors: {
    first_name?: string;
    last_name?: string;
    phone?: {
      country?: string;
      number?: string;
    };
  };
  touched: {
    first_name?: boolean;
    last_name?: boolean;
    phone?: {
      country?: boolean;
      number?: boolean;
    };
  };
  selectedDialCode: string;
  updateLoadingState: {
    status: string;
    message?: string;
  };
}

const AccountFormFields = ({
  user,
  dirty,
  handleChange,
  handleBlur,
  values,
  errors,
  touched,
  selectedDialCode,
  updateLoadingState
}: AccountFormFieldsProps) => {
  const { t } = useTranslation();

  return (
    <ContainerForm maxWidth="sm">
      <Typography
        variant="subtitle2"
        color="textSecondary"
        align="center"
        gutterBottom
        style={{ marginBottom: '1rem' }}
      >
        ({t('profile.account_register_info_text')}
        <b>{user['e-mail']}</b>)
      </Typography>

      <Typography variant="h4" component="h5">
        {t('profile.personal_information')}
      </Typography>
      <RouteLeavingCheck when={dirty} />
      <form>
        <TextField
          label={t('profile.first_name')}
          fullWidth
          id="firstName"
          margin="normal"
          name="first_name"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.first_name}
          required
          error={Boolean(errors.first_name) && touched.first_name}
          helperText={errors.first_name && touched.first_name && t(`form.validate.${errors.first_name}`)}
        />
        <TextField
          label={t('profile.last_name')}
          fullWidth
          id="lastName"
          margin="normal"
          name="last_name"
          value={values.last_name}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={Boolean(errors.last_name) && touched.last_name}
          helperText={errors.last_name && touched.last_name && t(`form.validate.${errors.last_name}`)}
        />
        <Select
          value={values.phone.country}
          fullWidth
          handleChange={handleChange}
          label={t('profile.country_code')}
          name="phone.country"
          id="phoneCountry"
        >
          {DialCodes.map(country => (
            <MenuItem key={country.code} value={country.code}>
              {`${t(country.name)} (${country.dial_code})`}
            </MenuItem>
          ))}
        </Select>
        <TextField
          label={t('profile.phone')}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start">{selectedDialCode}</InputAdornment>
          }}
          id="phoneNumber"
          margin="normal"
          name="phone.number"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.phone.number}
          required
          error={Boolean(((errors || {}).phone || {}).number) && ((touched || {}).phone || {}).number}
          helperText={
            ((errors || {}).phone || {}).number &&
            ((touched || {}).phone || {}).number &&
            t(`form.validate.${errors?.phone?.number}`)
          }
        />
        <Typography variant="h4" component="h5" gutterBottom style={{ marginTop: '2rem' }}>
          {t('profile.account_settings')}
        </Typography>
        <Select
          value={values.language}
          handleChange={handleChange}
          fullWidth
          label={t('profile.languages.language')}
          name="language"
          id="language"
        >
          {LANGUAGES.map(language => (
            <MenuItem key={language.value} value={language.value}>
              {t(language.label)}
            </MenuItem>
          ))}
        </Select>
        <Grid container sx={{ mt: '24px', mb: '24px' }}>
          <Checkbox
            data-testid="weekly-metrics-emails-checkbox"
            checked={values.allow_weekly_machine_summary_notifications}
            activeColor={COLOR_PRIMARY}
            name="allow_weekly_machine_summary_notifications"
            id="allow_weekly_machine_summary_notifications"
            label={t('profile.allow_weekly_metrics_emails')}
            className=""
            disabled={false}
            onChange={handleChange}
          />
        </Grid>
        <Button
          component={Link}
          variant="outlined"
          size="large"
          style={{
            marginTop: '1.5rem',
            paddingTop: '1rem',
            paddingBottom: '1rem'
          }}
          to="/account/reset-password"
        >
          {t('profile.change_password')}
        </Button>
        {isError(updateLoadingState.status) && <FormAlert message={t('errors.form_submit')} />}
      </form>
    </ContainerForm>
  );
};

export default AccountFormFields;
